import { useState } from "react";
import JSZip from "jszip";
import { apiService } from "@/services";
import { toast } from "@/hooks/use-toast";
import { buildLandscapeCsv, downloadBlob, landscapeCsvFilename } from "@/utils/landscapeCsvExport";
import type { ConsolidatedLandscape, LandscapeOutputData } from "@/types";

const EXPORT_CONCURRENCY = 5;

type ExportTarget = { phrase: string; instanceId: string };

export function useBulkLandscapeExport(landscapes: ConsolidatedLandscape[]) {
  const [isExporting, setIsExporting] = useState(false);

  const handleBulkExport = async (selectedIds: Set<string>, onComplete?: () => void) => {
    const targets = landscapes
      .filter((l) => selectedIds.has(l.id))
      .map((l) => ({ phrase: l.prioritySearchPhrase, instanceId: l.instances[0]?.id }))
      .filter((t): t is ExportTarget => !!t.instanceId);

    if (targets.length === 0) return;

    setIsExporting(true);

    // A single selected landscape downloads as a plain CSV, same as the
    // existing single-landscape export -- only multi-select needs zipping.
    if (targets.length === 1) {
      const target = targets[0];
      try {
        const data = await apiService.get<LandscapeOutputData>(`/landscapes/${target.instanceId}`);
        downloadBlob(new Blob([buildLandscapeCsv(data)], { type: "text/csv;charset=utf-8;" }), landscapeCsvFilename(data));
        toast({
          title: "Export complete",
          description: `Exported ${target.phrase} as a CSV file.`,
        });
      } catch {
        toast({
          title: "Export failed",
          description: `Could not fetch data for ${target.phrase}.`,
          variant: "destructive",
        });
      }
      setIsExporting(false);
      onComplete?.();
      return;
    }

    const failedPhrases: string[] = [];
    // Collect fetch results first, then build the zip in one synchronous
    // pass below -- filename de-duplication needs to check-then-add against
    // a shared Set, and doing that inside the concurrent Promise.all below
    // would race (two fetches resolving in the same microtask batch could
    // both see the name as free before either claims it).
    const fetched: { target: ExportTarget; data: LandscapeOutputData }[] = [];

    for (let i = 0; i < targets.length; i += EXPORT_CONCURRENCY) {
      const batch = targets.slice(i, i + EXPORT_CONCURRENCY);
      const results = await Promise.all(
        batch.map(async (target) => {
          try {
            const data = await apiService.get<LandscapeOutputData>(`/landscapes/${target.instanceId}`);
            return { target, data };
          } catch {
            failedPhrases.push(target.phrase);
            return null;
          }
        }),
      );
      for (const result of results) {
        if (result) fetched.push(result);
      }
    }

    const zip = new JSZip();
    const usedNames = new Set<string>();
    let successCount = 0;

    for (const { target, data } of fetched) {
      let filename = landscapeCsvFilename(data);
      // Two landscapes can slugify to the same filename (same phrase, or a
      // phrase that only differs in characters the slug strips) -- fall
      // back to a per-instance-unique name instead of silently clobbering
      // one file with another inside the zip.
      if (usedNames.has(filename)) {
        filename = filename.replace(/\.csv$/, `-${target.instanceId}.csv`);
      }
      usedNames.add(filename);
      zip.file(filename, buildLandscapeCsv(data));
      successCount++;
    }

    if (successCount === 0) {
      toast({
        title: "Export failed",
        description: "Could not fetch data for any selected landscape.",
        variant: "destructive",
      });
      setIsExporting(false);
      return;
    }

    const blob = await zip.generateAsync({ type: "blob" });
    const date = new Date().toISOString().slice(0, 10);
    downloadBlob(blob, `landscapes-export-${date}.zip`);

    if (failedPhrases.length > 0) {
      toast({
        title: "Export completed with some errors",
        description: `Exported ${successCount} of ${targets.length} landscapes. Failed: ${failedPhrases.join(", ")}.`,
      });
    } else {
      toast({
        title: "Export complete",
        description: `Exported ${successCount} landscape${successCount !== 1 ? "s" : ""} as a zip file.`,
      });
    }

    setIsExporting(false);
    onComplete?.();
  };

  return { isExporting, handleBulkExport };
}
